import React, { useState, useEffect } from 'react';
import { Button, Input, Checkbox, Form } from 'antd';
import { GoogleLogin } from 'react-google-login';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './PracticesTestLogin.scss';
import peopleImage from '../../assets/images/people.png';
import codehallLogo from '../../assets/images/logo.svg';
import { loginUsingGoogle, requestOTP, loginOTP } from '../../store/actions/AuthActions';
import { fetchPracticeTests } from '../../store/actions/PracticeTestActions';
import { isLoggedIn } from '../../helperFunctions';

const { Item } = Form;

const PracticesTestLogin = () => {
  const { t } = useTranslation('practice_tests');
  const dispatch = useDispatch();
  const { otp, user_id } = useSelector(state => state.auth);

  const [email, setEmail] = useState('');
  const [otpValue, setOtpValue] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (otp.status === 'invalid' || otp.status === 'expired') {
      setError(true);
    } else {
      setError(false);
    }
  }, [otp.status]);

  const handleGoogleResponse = response => {
    const payload = {
      provider: 'google',
      code: response.code,
      company_id: '9999',
      callback: () => {
        // Handle successful login callback
        console.log('Google login successful');
        dispatch(fetchPracticeTests());
      },
    };
    dispatch(loginUsingGoogle(payload));
  };

  const handleRequestOTP = () => {
    dispatch(requestOTP(email, null, '9999'));
  };

  const handleOtpChange = value => {
    setOtpValue(value);
  };

  const handleOTPSubmit = () => {
    dispatch(loginOTP(email, otpValue));
  };

  // Redirect to practice tests page if already logged in
  if (isLoggedIn(user_id)) {
    return <Redirect to="/practices-test" />;
  }

  return (
    <div className={styles['login-container']}>
      <div className={styles['login-wrapper']}>
        <div className={styles['login-form-container']}>
          <div className={styles['login-form-wrapper']}>
            <div className={styles['login-form-header']}>
              <h1 className={styles['login-title']}>{t('practice_tests.login.title')}</h1>
            </div>

            {otp.status === 'send' || otp.status === 'invalid' || otp.status === 'expired' ? (
              // OTP Form
              <div className={styles['otp-form-container']}>
                <div className={styles['otp-form-header']}>
                  <h2 className={styles['otp-title']}>{t('practice_tests.login.otp_title')}</h2>
                  <p className={styles['otp-subtitle']}>
                    {t('practice_tests.login.otp_subtitle', { email })}
                  </p>
                </div>

                <div className={styles['otp-input-container']}>
                  <Input.OTP
                    formatter={v => v.replace(/\D/g, '')}
                    length={6}
                    value={otpValue}
                    onChange={handleOtpChange}
                    status={error ? 'error' : undefined}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && otpValue.length === 6) {
                        handleOTPSubmit();
                      }
                    }}
                    className={styles['otp-input']}
                  />
                  {error && <div className={styles['otp-error']}>{otp.message}</div>}
                </div>

                <Button
                  type="primary"
                  onClick={handleOTPSubmit}
                  disabled={otpValue.length !== 6}
                  className={styles['otp-submit-button']}
                >
                  {t('practice_tests.login.submit')}
                </Button>

                <div className={styles['resend-code']}>
                  {/* Resend code functionality would go here */}
                  <Button
                    type="link"
                    onClick={() => handleRequestOTP()}
                    className={styles['resend-button']}
                  >
                    {t('practice_tests.login.resend_code')}
                  </Button>
                </div>
              </div>
            ) : (
              // Email Form
              <Form
                className={styles['login-form']}
                layout="vertical"
              >
                <Item
                  label="Email"
                  className={styles['form-item']}
                >
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    className={styles['email-input']}
                  />
                </Item>

                <div className={styles['form-options']}>
                  <div className={styles['remember-me']}>
                    <Checkbox
                      checked={rememberMe}
                      onChange={e => setRememberMe(e.target.checked)}
                      className={styles['remember-checkbox']}
                    >
                      {t('practice_tests.login.remember_me')}
                    </Checkbox>
                  </div>
                </div>

                <Button
                  type="primary"
                  onClick={() => {
                    if (email.trim()) {
                      handleRequestOTP();
                    }
                  }}
                  className={styles['login-button']}
                  disabled={!email.trim()}
                >
                  {t('practice_tests.login.login')}
                </Button>

                <div className={styles['divider']}>
                  <div className={styles['divider-line']}></div>
                  <span className={styles['divider-text']}>OR</span>
                </div>

                <GoogleLogin
                  className={styles['google-button']}
                  clientId="73660977592-m50kcgualicen86vokvsbg3vsh8h6c2e.apps.googleusercontent.com"
                  buttonText="Sign in with Google"
                  responseType="code"
                  onSuccess={response => handleGoogleResponse(response)}
                  onFailure={response => handleGoogleResponse(response)}
                />
              </Form>
            )}
          </div>
        </div>

        <div className={styles['login-illustration']}>
          <div className={styles['illustration-content']}>
            <div className={styles['logo-section']}>
              <img
                src={codehallLogo}
                alt="Codehall Logo"
                className={styles['logo']}
              />
            </div>

            <div className={styles['people-section']}>
              <img
                src={peopleImage}
                alt="Three people illustration"
                className={styles['people-image']}
              />
            </div>

            <div className={styles['illustration-text']}>
              <p className={styles['main-text']}>
                {t('practice_tests.login.smart')}{' '}
                <span className={styles['bold-text']}>{t('practice_tests.login.assessments')}</span>
              </p>
              <p className={styles['main-text']}>
                {t('practice_tests.login.for_smarter')}{' '}
                <span className={styles['bold-text']}>{t('practice_tests.login.hiring')}</span>
              </p>
              <p className={styles['sub-text']}>{t('practice_tests.login.thank_you')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PracticesTestLogin;
